import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { appendAssistantText, appendAssistantToolCall, patchToolCallResult } from '@/lib/chatEntryHelpers'
import { useChatStream } from '@/lib/useChatStream'
import type { AgentSessionProps, ChatEntry, TodoProps } from '@/types'

export function useChatMessages(
  initialMessages: ChatEntry[],
  initialTodos: TodoProps[],
  initialStreaming: boolean,
  activeSessionId: string | null,
  activeSession: AgentSessionProps | null,
  updateSessionInList: (session: AgentSessionProps) => void,
  setActiveSession: (updater: (prev: AgentSessionProps | null) => AgentSessionProps | null) => void,
) {
  const [entries, setEntries] = useState<ChatEntry[]>(initialMessages)
  const [todos, setTodos] = useState<TodoProps[]>(initialTodos)
  const [streaming, setStreaming] = useState(initialStreaming)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [entries])

  const refreshPersistedState = useCallback(() => {
    if (!activeSessionId) return
    fetch(`/sessions/${activeSessionId}/runtime`, { headers: { Accept: 'application/json' } })
      .then(r => r.json())
      .then((payload: { session: AgentSessionProps; messages: ChatEntry[]; todos: TodoProps[] }) => {
        setEntries(payload.messages)
        setTodos(payload.todos)
        updateSessionInList(payload.session)
      })
      .catch(() => {})
  }, [activeSessionId, updateSessionInList])

  const isRunning = useMemo(
    () => !!activeSession && ['queued', 'running'].includes(activeSession.status),
    [activeSession]
  )

  useChatStream(
    activeSessionId,
    isRunning,
    useMemo(() => ({
      onContent(content: string) {
        setEntries((prev) => appendAssistantText(prev, content))
      },
      onToolCall(tc) {
        setEntries((prev) => appendAssistantToolCall(prev, tc))
      },
      onToolCallUpdated(payload) {
        setEntries((prev) => patchToolCallResult(prev, payload))
      },
      onSessionUpdated(session) {
        updateSessionInList(session)
      },
      onTodosUpdated(updatedTodos) {
        setTodos(updatedTodos)
      },
      onError(error) {
        setActiveSession((prev) => prev ? { ...prev, last_error: error } : prev)
      },
      onDone() {
        setStreaming(false)
        refreshPersistedState()
      },
    }), [updateSessionInList, refreshPersistedState, setActiveSession])
  )

  return {
    entries,
    setEntries,
    todos,
    setTodos,
    streaming,
    setStreaming,
    messagesEndRef,
  }
}
